import { useControllingOverview } from "@vidario/api-client";
import { countLabel, ENTRY_NOUN, formatCost, formatCostString } from "@vidario/domain";
import { Card, EmptyState, Spinner } from "@vidario/ui";
import { Wallet } from "lucide-react";
import { type ReactElement } from "react";

import { useIsAdmin } from "../../guards/useRole";
import { JobCostRow } from "./JobCostRow";

/** Controlling (admin-only). Top level is a Job; expanding a Job reveals
 * its activities (e.g. "Rerun Longlist"), each broken down into Posten
 * (Claude tokens, SerpAPI/BrightData calls) with EUR costs. Going-forward
 * data only — costs are recorded per API call by the backend recorder. */
export function ControllingRoute(): ReactElement {
  const isAdmin = useIsAdmin();
  if (!isAdmin) return <NoAccess />;
  return <ControllingScreen />;
}

function ControllingScreen(): ReactElement {
  const { data, isLoading, error } = useControllingOverview();

  const currency = data?.unattributed.currency ?? "EUR";

  // React Compiler memoizes these derived values; no manual useMemo.
  const jobs = [...(data?.jobs ?? [])].sort((a, b) => Number(b.total) - Number(a.total));
  // Sum costs in integer minor units to avoid binary-float drift when
  // adding decimal-string amounts. Scale is 1e4 (not whole cents) so the
  // total keeps formatCost's max 4 fraction digits — the backend records
  // sub-cent per-call costs and rounding each to cents would truncate them.
  const COST_SCALE = 10_000;
  const toMinorUnits = (v: string | number): number => Math.round(Number(v) * COST_SCALE);
  const jobsMinor = (data?.jobs ?? []).reduce((sum, j) => sum + toMinorUnits(j.total), 0);
  const unattributedTotal = Number(data?.unattributed.total ?? 0);
  const grandTotal = (jobsMinor + toMinorUnits(unattributedTotal)) / COST_SCALE;
  const hasUnattributed = unattributedTotal > 0;

  return (
    <main className="mx-auto max-w-4xl px-4 py-10 sm:px-8 pb-20">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="m-0 text-3xl font-bold -tracking-[0.5px]">Controlling</h1>
          <p className="mt-1 text-sm text-text-secondary">
            API-Kosten je Stelle, aufgeschlüsselt nach Aktivität und Posten.
          </p>
        </div>
        {data && (
          <div className="text-right">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
              Gesamt
            </div>
            <div className="tabular-nums text-2xl font-bold">
              {formatCost(grandTotal, currency)}
            </div>
          </div>
        )}
      </header>

      {isLoading && (
        <div className="flex items-center gap-2 px-1 py-6 text-sm text-text-secondary">
          <Spinner size={18} />
          <span>Kosten werden geladen…</span>
        </div>
      )}

      {error && (
        <p className="px-1 py-6 text-sm text-danger-600" role="alert">
          Kosten konnten nicht geladen werden.
        </p>
      )}

      {data && jobs.length === 0 && !hasUnattributed && (
        <EmptyState
          icon={<Wallet size={28} />}
          title="Noch keine Kosten erfasst"
          description="Sobald Sourcing-Läufe oder Job-Parsings laufen, erscheinen hier die anfallenden API-Kosten."
        />
      )}

      {data && (jobs.length > 0 || hasUnattributed) && (
        <Card padded={false} className="p-0 overflow-hidden">
          <div className="flex items-center gap-3 border-b border-border-default bg-neutral-50 px-5 py-2.5">
            <span className="w-4" aria-hidden="true" />
            <span className="flex-1 text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
              Stelle
            </span>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
              Einträge
            </span>
            <span className="w-28 text-right text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
              Kosten
            </span>
          </div>

          {jobs.map((summary) => (
            <JobCostRow key={summary.jobId} summary={summary} />
          ))}

          {hasUnattributed && data && (
            <div className="flex items-center gap-3 border-t border-border-default bg-neutral-50/60 px-5 py-3.5">
              <span className="w-4" aria-hidden="true" />
              <span className="flex-1 font-medium text-text-secondary">Nicht zugeordnet</span>
              <span className="text-xs text-text-tertiary">
                {countLabel(data.unattributed.entries, ENTRY_NOUN)}
              </span>
              <span className="tabular-nums w-28 text-right font-semibold">
                {formatCostString(data.unattributed.total, currency)}
              </span>
            </div>
          )}
        </Card>
      )}
    </main>
  );
}

function NoAccess(): ReactElement {
  return (
    <main className="mx-auto max-w-4xl px-4 py-10 sm:px-8">
      <EmptyState
        title="Kein Zugriff"
        description="Das Controlling ist nur für Administratoren sichtbar."
      />
    </main>
  );
}
